import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { getConnectionStatus, SocialConnection } from '../api/ayrshareApi';
import { useAuth } from '../AuthContext';

interface ConnectionCache {
  connections: SocialConnection[];
  timestamp: number;
}

type ConnectionsListener = (brandId: number, connections: SocialConnection[]) => void;

interface AyrshareConnectionContextType {
  getConnections: (brandId: number) => Promise<SocialConnection[]>;
  refreshConnections: (brandId: number) => Promise<SocialConnection[]>;
  isLoading: (brandId: number) => boolean;
  /** Hear every refreshed list for any brand — the publish buttons and
      channel headers all mount their own copy, and a link that changes in
      one place (a card's Connect, the Ayrshare dashboard in another tab)
      must reach all of them. Returns an unsubscribe. */
  subscribe: (cb: ConnectionsListener) => () => void;
}

// A tab that comes back into view re-reads every brand it has on screen —
// links change in the Ayrshare dashboard, on a phone, in another tab, and
// the backend's /connections call re-syncs from Ayrshare each time.
const REFOCUS_MIN_INTERVAL_MS = 20000;

const AyrshareConnectionContext = createContext<AyrshareConnectionContextType | null>(null);

const CACHE_TTL = 30000; // 30 seconds cache TTL

export const AyrshareConnectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const cacheRef = useRef<Map<number, ConnectionCache>>(new Map());
  const loadingRef = useRef<Map<number, boolean>>(new Map());
  const pendingRef = useRef<Map<number, Promise<SocialConnection[]>>>(new Map());
  const [, forceUpdate] = useState({});
  const listenersRef = useRef<Set<ConnectionsListener>>(new Set());
  const lastRefocusRef = useRef(0);

  const fetchConnections = useCallback(async (brandId: number): Promise<SocialConnection[]> => {
    try {
      loadingRef.current.set(brandId, true);
      forceUpdate({});

      const response = await getConnectionStatus(brandId, token || undefined);
      const connections = response.connections || [];

      // Update cache
      cacheRef.current.set(brandId, {
        connections,
        timestamp: Date.now(),
      });
      listenersRef.current.forEach((cb) => {
        try { cb(brandId, connections); } catch (e) { console.debug('connections listener failed', e); }
      });

      return connections;
    } catch (err) {
      console.error('Failed to fetch connections:', err);
      return [];
    } finally {
      loadingRef.current.set(brandId, false);
      pendingRef.current.delete(brandId);
      forceUpdate({});
    }
  }, [token]);

  const getConnections = useCallback(async (brandId: number): Promise<SocialConnection[]> => {
    // Check cache first
    const cached = cacheRef.current.get(brandId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.connections;
    }

    // Check if already fetching
    const pending = pendingRef.current.get(brandId);
    if (pending) {
      return pending;
    }

    // Start new fetch
    const promise = fetchConnections(brandId);
    pendingRef.current.set(brandId, promise);
    return promise;
  }, [fetchConnections]);

  const refreshConnections = useCallback(async (brandId: number): Promise<SocialConnection[]> => {
    // Force refresh by invalidating cache
    cacheRef.current.delete(brandId);
    return fetchConnections(brandId);
  }, [fetchConnections]);

  const isLoading = useCallback((brandId: number): boolean => {
    return loadingRef.current.get(brandId) || false;
  }, []);

  const subscribe = useCallback((cb: ConnectionsListener) => {
    listenersRef.current.add(cb);
    return () => { listenersRef.current.delete(cb); };
  }, []);

  // Tab return → re-sync every brand currently cached (= on screen), at most
  // once per REFOCUS_MIN_INTERVAL_MS so a flurry of focus events is one fetch.
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefocusRef.current < REFOCUS_MIN_INTERVAL_MS) return;
      lastRefocusRef.current = now;
      Array.from(cacheRef.current.keys()).forEach((brandId) => {
        cacheRef.current.delete(brandId);
        fetchConnections(brandId);
      });
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [fetchConnections]);

  return (
    <AyrshareConnectionContext.Provider value={{ getConnections, refreshConnections, isLoading, subscribe }}>
      {children}
    </AyrshareConnectionContext.Provider>
  );
};

export const useAyrshareConnections = () => {
  const context = useContext(AyrshareConnectionContext);
  if (!context) {
    throw new Error('useAyrshareConnections must be used within AyrshareConnectionProvider');
  }
  return context;
};

// Hook for components to get connections with loading state
export const useBrandConnections = (brandId: number | undefined) => {
  const { getConnections, isLoading } = useAyrshareConnections();
  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!brandId) {
      setConnections([]);
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetch = async () => {
      setLoading(true);
      const result = await getConnections(brandId);
      if (mounted) {
        setConnections(result);
        setLoading(false);
      }
    };

    fetch();

    return () => {
      mounted = false;
    };
  }, [brandId, getConnections]);

  return { connections, loading: loading || (brandId ? isLoading(brandId) : false) };
};

export default AyrshareConnectionContext;
